import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LOGOS } from "../../data/ventures";

const LINKS = [
  { label: "Ventures", hash: "#ventures" },
  { label: "Manifesto", hash: "#manifesto" },
  { label: "Numbers", hash: "#numbers" },
  { label: "Contact", hash: "#contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const go = (hash) => {
    setOpen(false);
    if (location.pathname === "/") {
      const el = document.querySelector(hash);
      if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -70 });
    } else {
      navigate("/", { state: { scrollTo: hash } });
    }
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "glass" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-[1440px] items-center justify-between px-6 py-4 md:px-12">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <img
            src={LOGOS.veerox}
            alt="Veerox Group"
            className="h-10 w-10 rounded-lg object-cover ring-1 ring-white/10"
          />
          <div className="leading-tight">
            <span className="font-display text-lg font-bold tracking-tight text-white">
              VEEROX <span className="text-gradient-cyan">GROUP</span>
            </span>
            <div className="font-mono-tech text-[0.6rem] uppercase tracking-[0.28em] text-slate-400">
              Built for Success
            </div>
          </div>
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          {LINKS.map((l) => (
            <button
              key={l.hash}
              onClick={() => go(l.hash)}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="text-sm font-medium text-slate-300 transition-colors duration-200 hover:text-cyan-300"
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={() => go("#contact")}
            data-testid="nav-cta"
            className="btn-primary rounded-full px-6 py-2.5 font-display text-sm font-semibold text-white"
          >
            Work With Us
          </button>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="text-white md:hidden"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="glass border-t border-white/10 px-6 pb-6 pt-2 md:hidden" data-testid="nav-mobile-menu">
          {LINKS.map((l) => (
            <button
              key={l.hash}
              onClick={() => go(l.hash)}
              className="block w-full py-3 text-left text-base font-medium text-slate-200"
            >
              {l.label}
            </button>
          ))}
        </div>
      )}
    </motion.header>
  );
};
